import type { TemplateResult } from 'lit';
import { css, html, LitElement, nothing } from 'lit';
import { customElement, property } from 'lit/decorators.js';

import '../elements/d-section';
import '../elements/d-wrap';
import '../elements/d-action';
import '../fields/d-view-text';
import '../fields/d-view-html';
import '../fields/d-view-date';
import '../fields/d-view-assignees';
import '../fields/d-view-info';
import '../fields/d-conditional-bullet';
import '../editors/elements/d-edit-text';
import '../editors/elements/d-edit-textarea';
import '../editors/elements/d-select-dropdown';
import '../editors/components/d-select-add';
import '../editors/elements/d-select-tag';
import '../editors/elements/d-select-radio';
import '../editors/elements/d-select-checkbox';
import '../editors/elements/d-select-date';
import '../editors/components/d-edit-html';
import '../editors/components/d-edit-access';
import '../../content/partners/d-edit-partner-access';
import '../../content/employees/d-select-profession';
import '../../content/risk-assessments/d-view-risk';
import '../../content/risk-assessments/d-view-risk-simple';
import '../../content/substances/d-ghs-hazard-label';
import { LocalDate } from 'src/utilities/local-date';
import { isEmptyHtmlContent, isEmptyOrInvalidString } from 'src/utilities/text';
import type { SelectDropdownOption } from '../editors/elements/select-dropdown-option';
import type { DateTimeFormat } from './date-time-format';
import type {
  CustomField,
  EditCheckbox,
  EditDate,
  EditHtml,
  EditSelect,
  EditSelectAdd,
  EditText,
  EditTextarea,
  Expansion,
  Header,
  SectionField,
  ViewAssignees,
  ViewConditionalBulletList,
  ViewDate,
  ViewHazardLabels,
  ViewHtml,
  ViewInfo,
  ViewRisk,
  ViewRiskSimple,
  ViewSmartTaskInfo,
  ViewText,
} from './section-field';
import { unsafeHTML } from 'lit/directives/unsafe-html.js';
import { htmlContentStyles } from 'src/library/html-content-styles';

/**
 * Renders a responsive collection of html view fields and input fields based on a list of fields.
 * Can have dynamically expanding parts.
 * Automatically clears values of input fields in collapsed parts.
 */
@customElement('d-field-section')
export class DFieldSection extends LitElement {
  static readonly styles = [
    htmlContentStyles,
    css`
      :host {
        display: block;
        margin-top: -1px;
      }
      .cropper {
        overflow: clip;
      }
      .wrapper {
        display: flex;
        flex-wrap: wrap;
        margin: 1px -12px 0 -12px;
      }
      .wrapper > *:not(.break) {
        flex-grow: 1;
        flex-basis: 10%;
        /* border-image provides border-left */
        border-image-source: url(/images/section-element-border-image.svg);
        /* border-top-width appears as padding-top */
        border-top-width: 12px;
        /* border-bottom-width appears as padding-bottom */
        border-bottom-width: 10px;
        border-left-width: 1px;
        border-right-width: 0;
        border-style: solid;
        border-image-slice: 25%;
        border-image-repeat: repeat;
        margin-top: 1px;
        padding: 0 12px;
        /* box-shadow appears as border-top */
        box-shadow: 0 -1px var(--borderColor);
      }
      d-expansion .wrapper > *:not(.break) {
        margin-top: 0;
      }
      :host([theme-page]) .wrapper > * {
        box-shadow: 0 -1px var(--borderColorOnGray);
      }
      .break {
        min-width: calc(100% - 24px);
      }
      .wrapper > d-label[verybig] {
        border-top-width: 18px;
        border-bottom-width: 16px;
      }
      .minWidth200 {
        min-width: 200px;
      }
      .minWidth300 {
        min-width: 300px;
      }
      .fullWidth {
        min-width: calc(100% - 24px);
      }
      d-expansion > div {
        margin-top: -13px;
        margin-bottom: -12px;
      }
      .conditional-bullet-list d-label {
        display: block;
        margin-bottom: 4px;
      }
      div[slot='under-label'] d-bar-graph {
        margin-top: 8px;
      }
      div[slot='under-label'] p.description {
        margin: -2px 0 4px 0;
        font-family: var(--mainSans);
        font-weight: normal;
        font-size: 15px;
      }
      div[slot='under-label'] p.section-description {
        margin: 4px 0 0 0;
        font-family: var(--mainSans);
        font-weight: normal;
        font-size: 15px;
        line-height: 140%;
      }
      div[slot='under-label'] p.description.small {
        font-family: var(--mainSans);
        font-weight: normal;
        font-size: 14px;
        line-height: 140%;
        font-weight: 200;
      }
    `,
  ];

  @property({ type: String })
  entityType = '';
  @property({ type: Array })
  fields: SectionField[] = [];
  @property({ type: Object })
  item: any = undefined;
  @property({ type: Boolean, attribute: 'theme-page' })
  themePage = false;
  @property({ type: Number })
  contentStickyTop = 0;

  updateItem(value: any, item: any, field: any) {
    if (field.valueType === 'number') {
      value = Number(value);
    }
    if (field.valueType === 'boolean') {
      if (value === 'true') {
        value = true;
      }
      if (value === 'false') {
        value = false;
      }
    }
    if (field.storedAsOptions) {
      field.options.forEach((o) => {
        let propertyValue = false;
        if (value.includes(o.value)) {
          propertyValue = true;
        }
        item[o.value] = propertyValue;
      });
      this.item = { ...item };
    } else if (field.onValueChanged) {
      this[field.onValueChanged](item);
    } else {
      if (field.storedAsSingle) {
        value = value[0];
      }
      this.item = { ...item, [field.field]: value };
    }
    this.dispatchEvent(
      new CustomEvent('item-changed', {
        bubbles: true,
        composed: true,
        detail: { value: this.item },
      }),
    );
  }

  handleAction(e) {
    console.log('handleAction', e.detail);
    e.stopPropagation();
    this.dispatchEvent(
      new CustomEvent('field-action', {
        bubbles: true,
        composed: true,
        detail: { value: e.detail },
      }),
    );
  }

  handleLabelAction(action: string | undefined) {
    if (action) {
      this.dispatchEvent(
        new CustomEvent('field-action', {
          bubbles: true,
          composed: true,
          detail: { value: action },
        }),
      );
    }
  }

  render() {
    if (this.item) {
      return html`${this.renderSection(this.entityType, this.item, this.fields)}`;
    }
    return html``;
  }

  protected renderElement(item: any, entityType: string, field: SectionField, reset: boolean): TemplateResult<1> {
    if (
      (!Object.hasOwn(field, 'condition') || field.condition) &&
      (!('excludeFromScreen' in field) || ('excludeFromScreen' in field && field.excludeFromScreen === false))
    ) {
      switch (field.type) {
        case 'break':
          return html`${this.renderBreak()}`;
        case 'header':
          return html`${this.renderSectionHeader(entityType, field as Header)}`;
        case 'view-text':
          return html`${this.renderViewText(item, entityType, field as ViewText)}`;
        case 'view-html':
          return html`${this.renderViewHtml(item, entityType, field as ViewHtml)}`;
        case 'view-date':
          return html`${this.renderViewDate(item, entityType, field as ViewDate)}`;
        case 'view-assignees':
          return html`${this.renderViewAssignees(item, entityType, field as ViewAssignees)}`;
        case 'view-info':
          return html`${this.renderViewInfo(entityType, field as ViewInfo)}`;
        case 'view-conditional-bullet-list':
          return html`${this.renderViewConditionalBulletList(item, entityType, field)}`;
        case 'view-risk':
          return html`${this.renderViewRisk(item, entityType, field)}`;
        case 'view-risk-simple':
          return html`${this.renderViewRiskSimple(item, field)}`;
        case 'view-hazard-labels':
          return html`${this.renderViewHazardLabels(field)}`;
        case 'view-smart-task-info':
          return html`${this.renderViewSmartTaskInfo(field)}`;
        case 'edit-text':
          return html`${this.renderEditText(item, entityType, field as EditText, reset)}`;
        case 'edit-date':
          return html`${this.renderSelectDate(item, entityType, field as EditDate, reset)}`;
        case 'edit-textarea':
          return html`${this.renderEditTextarea(item, entityType, field as EditTextarea, reset)}`;
        case 'edit-html':
          return html`${this.renderEditHtml(item, entityType, field as EditHtml, reset)}`;
        case 'edit-dropdown':
          return html`${this.renderSelectDropdown(item, entityType, field as EditSelect, reset)}`;
        case 'edit-select-add':
          return html`${this.renderSelectAdd(item, entityType, field as EditSelect, reset)}`;
        case 'edit-tags':
          return html`${this.renderSelectTag(item, entityType, field as EditSelect, reset)}`;
        case 'edit-radio':
          return html`${this.renderSelectRadio(item, entityType, field as EditSelect, reset)}`;
        case 'edit-checkboxes':
          return html`${this.renderCheckboxes(item, entityType, field as EditSelect, reset)}`;
        case 'edit-checkbox':
          return html`${this.renderCheckbox(item, entityType, field as EditCheckbox, reset)}`;
        default:
          return html``;
      }
    }
    return html``;
  }

  protected renderSection(type: string, item: any, fields: SectionField[], reset: boolean = false): TemplateResult<1> {
    return html` <div class="cropper">
      <div class="wrapper">
        ${fields.map((field: SectionField) => {
          return html`
            ${field.type === 'expansion'
              ? html`${this.renderExpansion(type, item, field, reset)} `
              : html`
                  ${field.type === 'custom'
                    ? html`${this.renderCustom(field as CustomField)} `
                    : html`${this.renderElement(item, type, field, reset)} `}
                `}
          `;
        })}
      </div>
    </div>`;
  }

  private getArrayFromSingularProperties(item: any, options: SelectDropdownOption[]): string[] {
    return options
      .filter((o) => {
        return item[o.value];
      })
      .map((o) => {
        return o.value;
      });
  }

  private getEditFieldValue(item: any, field: any) {
    if (field.storedAsOptions) {
      return this.getArrayFromSingularProperties(item, field.options);
    }
    if (field.storedAsSingle) {
      return [item[field.field]];
    }
    if (field.value) {
      return this[field.value](item);
    }
    if (field.valueType === 'boolean') {
      if (item[field.field] === true) {
        return 'true';
      }
      if (item[field.field] === false) {
        return 'false';
      }
    }
    return item[field.field];
  }

  private getViewFieldValue(item: any, field: any) {
    if (field.value) {
      return field.value();
    }
    const value = item[field.field] ?? '';
    if (field.options) {
      return (
        field.options.find((o) => {
          return o.value === value;
        })?.text ?? ''
      );
    }
    if (value === '' && field.noValueValue) {
      return field.noValueValue;
    }
    return value;
  }

  private getFormattedDate(value: string, format: DateTimeFormat) {
    if (value && format === 'date-short') {
      return LocalDate.fromString(value).toStringForDisplay();
    }
    return '';
  }

  private getFieldForElement(
    entityType: string,
    field:
      | ViewText
      | ViewHtml
      | ViewDate
      | ViewAssignees
      | ViewInfo
      | ViewRisk
      | EditText
      | EditHtml
      | EditTextarea
      | EditSelect
      | EditDate,
  ): string {
    if (field.hideLabel) {
      return '';
    }
    if (field.fieldForLabel) {
      if (field.fieldForLabel.includes('_')) {
        return field.fieldForLabel;
      }
      return entityType + '_' + field.fieldForLabel;
    }
    if (field.field.includes('_')) {
      return field.field;
    }
    return entityType + '_' + field.field;
  }

  private showConditionalBullet(item: any, field: ViewConditionalBulletList): boolean {
    const bulletWithValue = field.listItems.find((listItem) => {
      return item[listItem.field] !== null || !listItem.hideIfNull;
    });
    return bulletWithValue !== undefined;
  }

  private renderBreak(): TemplateResult<1> {
    return html`<div class="break"></div>`;
  }

  /*
  private sectionHeaderStyle(field: Header): string {
    if (field.sticky) {
      return 'position:sticky;top:' + (this.contentStickyTop - 2) + 'px;background-color:white;z-index:1';
    }
    return '';
  }
  */

  private renderSectionHeader(entityType: string, field: Header): TemplateResult<1> {
    return html` <d-label
      allow-wrap
      .big=${!field.size || field.size === 'big'}
      .verybig=${field.size === 'verybig'}
      .field=${entityType + '_' + field.field}
      .label=${field.label ?? ''}
      .subfield=${entityType + '_' + field.subfield}
      .sublabel=${field.sublabel ?? ''}
      class="fullWidth ${field.class ?? ''}"
    >
      ${field.underLabel ? html`<div slot="under-label" class="html">${unsafeHTML(field.underLabel)}</div>` : nothing}
    </d-label>`;
  }

  private renderViewTextElement(
    entityType: string,
    field: ViewText,
    value: string,
    classes: string = '',
  ): TemplateResult<1> {
    return html`
      <d-view-text
        .wrapLabel=${!field.noWrapLabel}
        ?inline-label="${field.inlineLabel}"
        .lightLabel=${field.lightLabel ?? false}
        .labelAction=${field.labelAction?.name ?? ''}
        .labelActionMini=${field.labelActionMini ?? false}
        .labelActionPlain=${field.labelActionPlain ?? false}
        .field=${this.getFieldForElement(entityType, field)}
        .label=${field.label ?? ''}
        .sublabel=${field.sublabel ?? ''}
        class="${classes}"
        .classes=${field.labelClass ?? ''}
        .value=${value + ''}
        @label-action=${() => this.handleLabelAction(field.labelAction?.action)}
      >
      </d-view-text>
    `;
  }

  private renderViewText(item: any, entityType: string, field: ViewText): TemplateResult<1> {
    const value = this.getViewFieldValue(item, field);
    if (!field.hideIfEmpty || !isEmptyOrInvalidString(item[field.field])) {
      if (field.action) {
        return html`
          <d-wrap split tight baseline class="fullWidth">
            <div>${this.renderViewTextElement(entityType, field, value)}</div>
            <div style="margin-right: -2px">
              <d-action style="margin: -5px 0 -6px 0" .input=${field.action} @action=${(e) => this.handleAction(e)}
                >${field.action.name}</d-action
              >
            </div>
          </d-wrap>
        `;
      }
      return html`${this.renderViewTextElement(entityType, field, value, field.class ?? '')}`;
    }
    return html``;
  }

  private renderViewHtml(item: any, entityType: string, field: ViewHtml): TemplateResult<1> {
    if (!field.hideIfEmpty || !isEmptyHtmlContent(item[field.field])) {
      return html`<d-view-html
        ?wrap-label="${field.wrapLabel}"
        .field=${this.getFieldForElement(entityType, field)}
        .label=${field.label ?? ''}
        .sublabel=${field.sublabel ?? ''}
        .contentId=${(item.uuid ?? '') + ':' + field.field}
        .value=${this.getViewFieldValue(item, field)}
        .expandable=${field.expandable ?? false}
        .action=${field.action}
        class="fullWidth ${field.class}"
        @action=${(e) => this.handleAction(e)}
      >
        ${field.underLabel ? html`<div slot="under-label" class="html">${unsafeHTML(field.underLabel)}</div>` : nothing}
      </d-view-html>`;
    }
    return html``;
  }

  private renderViewDate(item: any, entityType: string, field: ViewDate): TemplateResult<1> {
    const value = item[field.field];
    if (typeof value === 'string' && (!field.hideIfEmpty || value)) {
      return html`
        <d-view-date
          wrap-label
          .field=${this.getFieldForElement(entityType, field)}
          .label=${field.label ?? ''}
          class="${field.class ?? ''}"
          .value=${value}
          .format=${field.format ?? undefined}
          .today=${field.today ?? undefined}
          ?relative=${field.relativeDate ?? false}
        >
        </d-view-date>
      `;
    }
    return html``;
  }

  private renderViewAssignees(item: any, entityType: string, field: ViewAssignees): TemplateResult<1> {
    const assignees = item[field.field];
    return html`
      <d-view-assignees
        wrap-label
        .field=${this.getFieldForElement(entityType, field)}
        .label=${field.label ?? ''}
        class="${field.class ?? ''}"
        .peopleAndFunctions=${field.peopleAndFunctions ?? []}
        .assignees=${assignees}
        .fullNames=${field.fullNames ?? false}
        .pronoun=${field.pronoun ?? undefined}
      >
      </d-view-assignees>
    `;
  }

  private renderViewInfo(entityType: string, field: ViewInfo): TemplateResult<1> {
    return html`<d-view-info
      .field=${this.getFieldForElement(entityType, field)}
      .content=${field.content ?? ''}
      .label=${field.label ?? ''}
      class="fullWidth"
    >
    </d-view-info>`;
  }

  private renderViewConditionalBulletList(
    item: any,
    entityType: string,
    field: ViewConditionalBulletList,
  ): TemplateResult<1> {
    if (this.showConditionalBullet(item, field)) {
      return html` <div class="conditional-bullet-list fullWidth">
        ${field.field ? html` <d-label big .field=${entityType + '_' + [field.field]}></d-label> ` : nothing}
        ${field.listItems.map((listItem) => {
          const value = item[listItem.field];
          if (Object.hasOwn(listItem, 'condition') && listItem.condition === false) {
            return html``;
          }
          if (
            value === true ||
            (value === false && listItem.elseField) ||
            (value !== true && value !== false && listItem.elseField && !listItem.hideIfNull)
          ) {
            return html`
              <d-conditional-bullet
                .field=${entityType + '_' + listItem.field}
                .elseField=${entityType + '_' + listItem.elseField}
                .hideIfNull=${listItem.hideIfNull ?? false}
                .condition=${value}
                .alert=${value === false && listItem.alert ? true : false}
              ></d-conditional-bullet>
            `;
          }
        })}
      </div>`;
    }
    return html``;
  }

  private renderViewRisk(item: any, entityType: string, field: ViewRisk) {
    return html`<d-view-risk
      class="fullWidth"
      .field=${this.getFieldForElement(entityType, field)}
      .value=${this.getViewFieldValue(item, field)}
    ></d-view-risk>`;
  }

  private renderViewRiskSimple(item: any, field: ViewRiskSimple) {
    return html`<d-view-risk-simple .value=${this.getViewFieldValue(item, field)}></d-view-risk-simple>`;
  }

  private renderViewHazardLabels(field: ViewHazardLabels) {
    return html`<d-wrap>
      ${field.items.map((label) => html`<d-ghs-hazard-label .type=${label}></d-ghs-hazard-label>`)}
    </d-wrap>`;
  }

  private renderViewSmartTaskInfo(field: ViewSmartTaskInfo) {
    return html`<div>
      <d-smart-task-info
        class="fullWidth"
        .functionName=${field.functionName}
        .smartTaskEventText=${field.text}
      ></d-smart-task-info>
    </div>`;
  }

  private renderEditText(item: any, entityType: string, field: EditText, reset: boolean): TemplateResult<1> {
    const value = this.getEditFieldValue(item, field);
    if (reset && value !== '') {
      this.updateItem('', item, field);
    }
    return html`<d-edit-text
      ?wrap-label="${field.wrapLabel}"
      .field=${this.getFieldForElement(entityType, field)}
      .label=${field.label ?? ''}
      class="${field.class ?? ''}"
      .autofocus=${field.autofocus ?? false}
      .selectOnFocus=${field.selectOnFocus ?? false}
      placeholder="${field.placeholder ?? ''}"
      ?mark-if-empty="${field.markIfEmpty}"
      .value=${value}
      .themePage=${this.themePage}
      @value-changed=${(e) => {
        e.stopPropagation();
        this.updateItem(e.detail.value, item, field);
      }}
    >
      ${field.underLabel ? html`<div slot="under-label" class="html">${unsafeHTML(field.underLabel)}</div>` : nothing}
    </d-edit-text>`;
  }

  private renderEditTextarea(item: any, entityType: string, field: EditTextarea, reset: boolean): TemplateResult<1> {
    const value = this.getEditFieldValue(item, field);
    if (reset && value !== '') {
      this.updateItem('', item, field);
    }
    return html`
      <d-edit-textarea
        ?wrap-label="${field.wrapLabel}"
        .field=${this.getFieldForElement(entityType, field)}
        .label=${field.label ?? ''}
        class="${field.class ?? ''}"
        .autofocus=${field.autofocus ?? false}
        .selectOnFocus=${field.selectOnFocus ?? false}
        placeholder="${field.placeholder ?? ''}"
        .value=${value}
        .themePage=${this.themePage}
        wrap-label
        @value-changed=${(e) => {
          e.stopPropagation();
          this.updateItem(e.detail.value, item, field);
        }}
      >
        ${field.underLabel ? html`<div slot="under-label" class="html">${unsafeHTML(field.underLabel)}</div>` : nothing}
      </d-edit-textarea>
    `;
  }

  private renderEditHtml(item: any, entityType: string, field: EditHtml, reset: boolean): TemplateResult<1> {
    const value = this.getEditFieldValue(item, field);
    if (reset && value !== '') {
      this.updateItem('', item, field);
    }
    return html`
      <d-edit-html
        ?wrap-label="${field.wrapLabel}"
        .field=${this.getFieldForElement(entityType, field)}
        .docsForLinking=${field.docsForLinking}
        .label=${field.label ?? ''}
        .labelAction=${field.labelAction?.name ?? ''}
        .labelActionMini=${field.labelActionMini ?? false}
        .labelActionPlain=${field.labelActionPlain ?? false}
        class="fullWidth ${field.class ?? ''}"
        .contentStickyTop=${this.contentStickyTop}
        .autofocus=${field.autofocus ?? false}
        .selectOnFocus=${field.selectOnFocus ?? false}
        placeholder="${field.placeholder ?? ''}"
        .value=${value}
        .themePage=${this.themePage}
        @value-changed=${(e) => {
          e.stopPropagation();
          this.updateItem(e.detail.value, item, field);
        }}
        @label-action=${() => this.handleLabelAction(field.labelAction?.action)}
      ></d-edit-html>
    `;
  }

  private renderSelectDropdown(item: any, entityType: string, field: EditSelect, reset: boolean): TemplateResult<1> {
    const value = this.getEditFieldValue(item, field);
    if (reset && value !== '') {
      this.updateItem('', item, field);
    }
    return html`
      <d-select-dropdown
        ?wrap-label="${field.wrapLabel}"
        .field=${this.getFieldForElement(entityType, field)}
        .label=${field.label ?? ''}
        class="${field.class ?? ''}"
        .options=${field.options}
        placeholder="${field.placeholder ?? ''}"
        ?mark-if-empty="${field.markIfEmpty}"
        .value=${value}
        .themePage=${this.themePage}
        ?inline-label="${field.inlineLabel ?? false}"
        ?light-label="${field.lightLabel ?? false}"
        @value-changed=${(e) => {
          e.stopPropagation();
          this.updateItem(e.detail.value, item, field);
        }}
      >
        ${field.underLabel ? html`<div slot="under-label" class="html">${unsafeHTML(field.underLabel)}</div>` : nothing}
      </d-select-dropdown>
    `;
  }

  private renderSelectAdd(item: any, entityType: string, field: EditSelectAdd, reset: boolean): TemplateResult<1> {
    const value = this.getEditFieldValue(item, field);
    if (reset && value !== '') {
      this.updateItem('', item, field);
    }
    return html`
      <d-select-add
        ?wrap-label="${field.wrapLabel}"
        .allowRemoveAll=${field.allowRemoveAll ?? false}
        .field=${this.getFieldForElement(entityType, field)}
        .label=${field.label ?? ''}
        class="${field.class ?? ''}"
        .options=${field.options}
        placeholder="${field.placeholder ?? ''}"
        .selectedItems=${value}
        .themePage=${this.themePage}
        ?inline-label="${field.inlineLabel ?? false}"
        ?light-label="${field.lightLabel ?? false}"
        @selected-items-changed=${(e) => {
          e.stopPropagation();
          this.updateItem(e.detail.selectedItems, item, field);
        }}
      >
        ${field.underLabel ? html`<div slot="under-label" class="html">${unsafeHTML(field.underLabel)}</div>` : nothing}
      </d-select-add>
    `;
  }

  private renderSelectTag(item: any, entityType: string, field: EditSelect, reset: boolean): TemplateResult<1> {
    const value = this.getEditFieldValue(item, field);
    if (reset && value.length !== 0) {
      this.updateItem([], item, field);
    }
    if (field.options.length) {
      return html`
        <d-select-tag
          ?wrap-label="${field.wrapLabel}"
          .field=${this.getFieldForElement(entityType, field)}
          .label=${field.label ?? ''}
          class="${field.class ?? ''}"
          .options="${field.options}"
          .value=${value}
          ?single="${field.single}"
          .themePage=${this.themePage}
          @value-changed=${(e) => {
            e.stopPropagation();
            this.updateItem(e.detail.value, item, field);
          }}
        >
          ${field.underLabel
            ? html`<div slot="under-label" class="html">${unsafeHTML(field.underLabel)}</div>`
            : nothing}
        </d-select-tag>
      `;
    }
    return html``;
  }

  private renderSelectRadio(item: any, entityType: string, field: EditSelect, reset: boolean): TemplateResult<1> {
    const value = this.getEditFieldValue(item, field);
    if (reset && value !== null) {
      this.updateItem(null, item, field);
    }
    return html`
      <d-select-radio
        ?wrap-label="${field.wrapLabel}"
        .field=${this.getFieldForElement(entityType, field)}
        .label=${field.label ?? ''}
        class="${field.class ?? ''}"
        .options=${field.options}
        .value=${value}
        .themePage=${this.themePage}
        ?inline-label="${field.inlineLabel ?? false}"
        ?light-label="${field.lightLabel ?? false}"
        @value-changed=${(e) => {
          e.stopPropagation();
          this.updateItem(e.detail.value, item, field);
        }}
      >
        ${field.underLabel ? html`<div slot="under-label" class="html">${unsafeHTML(field.underLabel)}</div>` : nothing}
      </d-select-radio>
    `;
  }

  private renderCheckboxes(item: any, entityType: string, field: EditSelect, reset: boolean): TemplateResult<1> {
    const value = this.getEditFieldValue(item, field);
    if (reset && value.length !== 0) {
      this.updateItem([], item, field);
    }
    return html`
      <d-select-checkbox
        ?wrap-label="${field.wrapLabel}"
        .field=${this.getFieldForElement(entityType, field)}
        .label=${field.label ?? ''}
        class="${field.class ?? ''}"
        .options=${field.options}
        .value=${value}
        .themePage=${this.themePage}
        @value-changed=${(e) => {
          e.stopPropagation();
          this.updateItem(e.detail.value, item, field);
        }}
      >
        ${field.underLabel ? html`<div slot="under-label" class="html">${unsafeHTML(field.underLabel)}</div>` : nothing}
      </d-select-checkbox>
    `;
  }

  private renderSelectDate(item: any, entityType: string, field: EditDate, reset: boolean): TemplateResult<1> {
    const value = this.getEditFieldValue(item, field);
    if (reset && value !== '') {
      this.updateItem('', item, field);
    }
    return html`<d-select-date
      ?wrap-label="${field.wrapLabel}"
      .field=${this.getFieldForElement(entityType, field)}
      .label=${field.label ?? ''}
      ?clear="${field.clear}"
      ?today="${field.today}"
      class="${field.class ?? ''}"
      .value=${value}
      .themePage=${this.themePage}
      .disableBefore=${field.disableBefore ?? ''}
      .disableAfter=${field.disableAfter ?? ''}
      @value-changed=${(e) => {
        e.stopPropagation();
        this.updateItem(e.detail.value, item, field);
      }}
    >
      ${field.underLabel ? html`<div slot="under-label" class="html">${unsafeHTML(field.underLabel)}</div>` : nothing}
    </d-select-date>`;
  }

  private renderCheckbox(item: any, entityType: string, field: EditCheckbox, reset: boolean): TemplateResult<1> {
    const value = this.getEditFieldValue(item, field);
    if (reset && value !== false) {
      this.updateItem(false, item, field);
    }
    return html`<d-checkbox
      ?wrap-label="${field.wrapLabel}"
      .field=${field.optionField ? '' : entityType + '_' + field.field}
      .optionField=${field.optionField ? entityType + '_' + field.optionField : ''}
      .label=${field.label ?? ''}
      .optionLabel=${field.optionLabel ? field.optionLabel : ''}
      class="${field.class ?? ''}"
      ?hide-option-tooltip="${field.hideOptionTooltip}"
      wrap-label
      .checked=${value}
      .themePage=${this.themePage}
      @checked-changed=${(e) => {
        e.stopPropagation();
        this.updateItem(e.detail.checked, item, field);
      }}
    >
      ${field.underLabel ? html`<div slot="under-label" class="html">${unsafeHTML(field.underLabel)}</div>` : nothing}
    </d-checkbox>`;
  }

  private renderCustom(field: CustomField) {
    if (!Object.hasOwn(field, 'condition') || field.condition) {
      return field.render();
    }
    return html``;
  }

  private renderExpansion(type: string, item: any, field: Expansion, reset: boolean = false): TemplateResult<1> {
    if (!Object.hasOwn(field, 'condition') || field.condition) {
      if ((field.resetHiddenFields && !field.opened) || reset) {
        reset = true;
      } else {
        reset = false;
      }
      return html` <d-expansion ?opened="${field.opened}" class="fullWidth">
        <div style=${field.indent ? 'padding-left:' + field.indent + 'px' : ''}>
          ${this.renderSection(type, item, field.fields, reset)}
        </div>
      </d-expansion>`;
    }
    return html``;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'd-field-section': DFieldSection;
  }
}
